import React from 'react';
import { Typography } from 'antd';
import { LinkOutlined, SendOutlined, PlayCircleOutlined, PictureOutlined } from '@ant-design/icons';
import styles from './styles.module.less';

const { Text, Paragraph } = Typography;

interface FacebookButton {
  type: 'web_url' | 'postback';
  title: string;
  url?: string;
  payload?: string;
}

interface TemplatePreviewData {
  type?: string;
  title?: string;
  subtitle?: string;
  text?: string;
  image_url?: string;
  media_type?: string;
  media_url?: string;
  attachment_id?: string;
  aspect_ratio?: string;
  buttons?: FacebookButton[];
  default_action?: {
    type: string;
    url: string;
  };
}

interface TemplatePreviewProps {
  data?: TemplatePreviewData;
}

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({ data }) => {
  const templateType = data?.type;

  const handleButtonClick = (button: FacebookButton) => {
    if (button.type === 'web_url' && button.url) {
      window.open(button.url, '_blank');
    } else if (button.type === 'postback' && button.payload) {
      // Postback payload - can be handled by parent component if needed
    }
  };

  const renderButtons = (buttons: FacebookButton[] | undefined) => {
    if (!buttons || buttons.length === 0) return null;
    
    return (
      <div className={styles.buttonsContainer}>
        {buttons.slice(0, 3).map((btn, idx) => (
          <button
            key={idx}
            onClick={() => handleButtonClick(btn)}
            className={styles.button}
          >
            {btn.type === 'web_url' ? (
              <LinkOutlined style={{ fontSize: 14 }} />
            ) : (
              <SendOutlined style={{ fontSize: 14 }} />
            )}
            {btn.title || 'Button'}
          </button>
        ))}
      </div>
    );
  };

  const renderGenericTemplate = () => {
    const title = data?.title;
    const subtitle = data?.subtitle;
    const imageUrl = data?.image_url;
    const buttons = data?.buttons;
    const defaultAction = data?.default_action;

    const hasContent = title || subtitle || imageUrl || (buttons && buttons.length > 0);
    if (!hasContent) {
      return <Text type="secondary">Nhập nội dung để xem trước</Text>;
    }

    return (
      <div 
        className={styles.genericCard}
        onClick={() => defaultAction?.url && window.open(defaultAction.url, '_blank')}
      >
        {imageUrl ? (
          <div className={styles.imageContainer}>
            <img 
              src={imageUrl} 
              alt="preview" 
              className={styles.image}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        ) : (
          <div className={styles.imagePlaceholder}>
            <PictureOutlined style={{ fontSize: 32, color: "var(--text-muted-color)" }} />
            <Text style={{ color: "var(--text-muted-color)", fontSize: 12 }}>Thêm hình ảnh</Text>
          </div>
        )}

        <div className={styles.content}>
          <Paragraph 
            strong 
            className={styles.title}
            ellipsis={{ rows: 2 }}
          >
            {title || 'Tiêu đề'}
          </Paragraph>
          {subtitle && (
            <Paragraph 
              type="secondary" 
              className={styles.subtitle}
              ellipsis={{ rows: 2 }}
            >
              {subtitle}
            </Paragraph>
          )}
        </div>

        {renderButtons(buttons)}
      </div>
    );
  };

  const renderButtonTemplate = () => {
    const text = data?.text;
    const buttons = data?.buttons;

    const hasContent = text || (buttons && buttons.length > 0);
    if (!hasContent) {
      return <Text type="secondary">Nhập nội dung để xem trước</Text>;
    }

    return (
      <div className={styles.buttonTemplate}> 
        <div className={styles.messageBubble}>
          <Text style={{ fontSize: 15, whiteSpace: 'pre-wrap' }}>
            {text || 'Nội dung tin nhắn...'}
          </Text>
        </div>

        {buttons && buttons.length > 0 && (
          <div className={styles.buttonList}>
            {buttons.slice(0, 3).map((btn: FacebookButton, idx: number) => (
              <button
                key={idx}
                onClick={() => handleButtonClick(btn)}
                className={styles.buttonOutlined}
              >
                {btn.type === 'web_url' ? (
                  <LinkOutlined style={{ fontSize: 14 }} />
                ) : (
                  <SendOutlined style={{ fontSize: 14 }} />
                )}
                {btn.title || 'Button'}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderMediaTemplate = () => {
    const mediaType = data?.media_type || 'image';
    const mediaUrl = data?.media_url;
    const attachmentId = data?.attachment_id;
    const aspectRatio = data?.aspect_ratio || 'horizontal';
    const buttons = data?.buttons;

    const hasContent = mediaUrl || attachmentId || (buttons && buttons.length > 0);
    if (!hasContent) {
      return <Text type="secondary">Nhập nội dung để xem trước</Text>;
    }

    const height = aspectRatio === 'square' ? 280 : 147;

    return (
      <div className={styles.mediaCard}>
        {mediaType === 'video' ? (
          <div className={styles.videoContainer} style={{ height }}>
            {mediaUrl ? (
              <video 
                src={mediaUrl}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                controls={false}
              />
            ) : (
              <>
                <PlayCircleOutlined style={{ fontSize: 48, color: '#fff' }} />
                <Text style={{ color: '#fff', fontSize: 12 }}>
                  {attachmentId ? `ID: ${attachmentId}` : 'Video Preview'}
                </Text>
              </>
            )}
            <div className={styles.playOverlay}>
              <PlayCircleOutlined style={{ fontSize: 32, color: '#fff' }} />
            </div>
          </div>
        ) : (
          mediaUrl ? (
            <img 
              src={mediaUrl} 
              alt="preview" 
              className={styles.image}
              style={{ height }} 
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div className={styles.imagePlaceholder} style={{ height }}>
              <PictureOutlined style={{ fontSize: 32, color: '#fff' }} />
              <Text style={{ color: '#fff', fontSize: 12 }}>
                {attachmentId ? `ID: ${attachmentId}` : 'Thêm hình ảnh'}
              </Text>
            </div>
          )
        )}
        {renderButtons(buttons)}
      </div>
    );
  };

  const renderContent = () => {
    if (!data || !templateType) {
      return (
        <div className={styles.emptyState}>
          <Text type="secondary">Chọn loại template để xem trước</Text>
        </div>
      );
    }

    switch (templateType) {
      case 'GENERIC':
        return renderGenericTemplate();
      case 'BUTTON':
        return renderButtonTemplate();
      case 'MEDIA':
        return renderMediaTemplate();
      default:
        return <Text type="secondary">Loại template không hỗ trợ</Text>;
    }
  };

  return (
    <div className={styles.previewContainer}>
      <div className={styles.messengerHeader}>
        <div className={styles.avatar}>
          K
        </div>
        <div className={styles.headerInfo}>
          <Text className={styles.headerTitle}>KIDO CRM</Text>
          <Text className={styles.headerStatus}>Đang hoạt động</Text>
        </div>
      </div>

      <div className={styles.chatArea}>
        {renderContent()}
      </div>

      <div className={styles.inputMockup}>
        <div className={styles.inputPlaceholder}>
          Aa
        </div>
        <SendOutlined className={styles.sendIcon} />
      </div>
    </div>
  );
};

